import { jsPDF } from 'jspdf'

import {
  hexToRgb,
  type PdfFontWeight,
  registerGeistFonts,
  setGeistFont,
} from '@/lib/pdf/fonts'
import { formatDate, numberToCurrency } from '@/utils'
import { MINUTES_PER_PAGE, RATE_PER_HOUR } from '../../../billing'
import type { CaseBilling } from './billingRollups'

export interface AccountSummaryData {
  accountName: string
  plan: string
  periodLabel: string
  billableCases: number
  billablePages: number
  billableHours: number
  billableAmount: number
  accountCreatedAt: string
  cases: CaseBilling[]
}

const money = (value: number) =>
  numberToCurrency(value, { maximumFractionDigits: 0 })

const COLORS = {
  heading: '#0f172a',
  body: '#1e293b',
  secondary: '#475569',
  muted: '#94a3b8',
  border: '#e2e8f0',
  bgSubtle: '#f8fafc',
  accent: '#2563eb',
  accentBg: '#eff6ff',
  accentBar: '#3b82f6',
  white: '#ffffff',
} as const

async function svgToPng(
  svgUrl: string,
  width: number,
  height: number,
): Promise<string> {
  const response = await fetch(svgUrl)
  const svgText = await response.text()

  const canvas = document.createElement('canvas')
  const scale = 3
  canvas.width = width * scale
  canvas.height = height * scale

  const ctx = canvas.getContext('2d')!
  const img = new Image()
  const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  return new Promise((resolve, reject) => {
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = reject
    img.src = url
  })
}

class PDFReport {
  private pdf: jsPDF
  private y = 0
  private readonly m = 20
  private readonly pageW = 210
  private readonly w: number

  constructor() {
    this.pdf = new jsPDF('p', 'mm', 'a4')
    this.w = this.pageW - this.m * 2
    registerGeistFonts(this.pdf)
    this.y = this.m + 4
  }

  private font(weight: PdfFontWeight, size: number, color: string) {
    this.pdf.setTextColor(...hexToRgb(color))
    this.pdf.setFontSize(size)
    setGeistFont(this.pdf, weight)
  }

  private textAt(
    str: string,
    x: number,
    weight: PdfFontWeight,
    size: number,
    color: string,
    align: 'left' | 'right' | 'center' = 'left',
  ) {
    this.font(weight, size, color)
    this.pdf.text(str, x, this.y, { align })
  }

  private fill(x: number, y: number, w: number, h: number, color: string) {
    this.pdf.setFillColor(...hexToRgb(color))
    this.pdf.rect(x, y, w, h, 'F')
  }

  private roundedFill(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    color: string,
  ) {
    this.pdf.setFillColor(...hexToRgb(color))
    this.pdf.roundedRect(x, y, w, h, r, r, 'F')
  }

  private roundedStroke(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    fill: string,
    stroke: string,
  ) {
    this.pdf.setFillColor(...hexToRgb(fill))
    this.pdf.setDrawColor(...hexToRgb(stroke))
    this.pdf.setLineWidth(0.2)
    this.pdf.roundedRect(x, y, w, h, r, r, 'FD')
  }

  private hline() {
    this.pdf.setDrawColor(...hexToRgb(COLORS.border))
    this.pdf.setLineWidth(0.2)
    this.pdf.line(this.m, this.y, this.m + this.w, this.y)
  }

  drawHeader(data: AccountSummaryData) {
    const now = formatDate(new Date(), 'MMMM d, yyyy')

    this.textAt(
      data.accountName || 'Account Summary',
      this.m,
      'bold',
      20,
      COLORS.heading,
    )
    this.y += 6
    this.textAt('Billing Statement', this.m, 'normal', 10, COLORS.secondary)

    // Plan badge — top right, centered text in pill
    const planLabel = `${data.plan || 'N/A'} Plan`
    this.font('semibold', 8.5, COLORS.accent)
    const labelW = this.pdf.getTextWidth(planLabel)
    const badgePad = 5
    const badgeW = labelW + badgePad * 2
    const badgeX = this.m + this.w - badgeW
    const badgeY = this.y - 13
    this.roundedFill(badgeX, badgeY, badgeW, 6, 1.5, COLORS.accentBg)
    this.y = badgeY + 4
    this.textAt(
      planLabel,
      badgeX + badgePad + labelW / 2,
      'semibold',
      8.5,
      COLORS.accent,
      'center',
    )

    this.y = badgeY + 10
    this.textAt(
      `Account since ${formatDate(data.accountCreatedAt) || 'N/A'}`,
      this.m + this.w,
      'normal',
      8.5,
      COLORS.muted,
      'right',
    )
    this.y += 4.5
    this.textAt(
      `Generated ${now}`,
      this.m + this.w,
      'normal',
      8.5,
      COLORS.muted,
      'right',
    )

    // Period label
    this.y += 4.5
    this.textAt(
      `Period: ${data.periodLabel}`,
      this.m + this.w,
      'medium',
      8.5,
      COLORS.secondary,
      'right',
    )

    this.y += 6
    this.hline()
    this.y += 8
  }

  drawMetrics(data: AccountSummaryData) {
    const cards = [
      {
        label: 'Billable Cases',
        value: data.billableCases.toLocaleString(),
        sub: data.periodLabel,
      },
      {
        label: 'Pages Analyzed',
        value: data.billablePages.toLocaleString(),
        sub: 'Across billable cases',
      },
      {
        label: 'Billable Hours',
        value: `${data.billableHours.toFixed(1)} hrs`,
        sub: `@ ${MINUTES_PER_PAGE} min / page`,
      },
      {
        label: 'Billable Amount',
        value: money(data.billableAmount),
        sub: `@ $${RATE_PER_HOUR} / hr`,
      },
    ]

    const gap = 3.5
    const cardW = (this.w - gap * 3) / 4
    const cardH = 24
    const startY = this.y

    cards.forEach((card, i) => {
      const x = this.m + i * (cardW + gap)
      this.roundedStroke(
        x,
        startY,
        cardW,
        cardH,
        2,
        COLORS.bgSubtle,
        COLORS.border,
      )

      this.y = startY + 6.5
      this.textAt(card.label, x + 4, 'medium', 7.5, COLORS.secondary)

      this.y = startY + 14
      this.textAt(card.value, x + 4, 'bold', 14, COLORS.heading)

      this.y = startY + 19.5
      this.textAt(card.sub, x + 4, 'normal', 6.5, COLORS.muted)
    })

    this.y = startY + cardH + 8
  }

  drawCaseTable(data: AccountSummaryData) {
    if (data.cases.length === 0) return

    const x = this.m
    const pad = 2
    const rowH = 7
    const pagesX = x + this.w * 0.72
    const amountX = x + this.w - pad

    this.textAt('Billing by case', x + pad, 'semibold', 9, COLORS.heading)
    this.y += 6

    this.textAt('Case', x + pad, 'medium', 7.5, COLORS.secondary)
    this.pdf.text('Pages', pagesX, this.y, { align: 'right' })
    this.pdf.text('Amount', amountX, this.y, { align: 'right' })
    this.y += 2
    this.hline()

    data.cases.forEach((entry, index) => {
      if (index % 2 === 0) {
        this.fill(x, this.y, this.w, rowH, COLORS.bgSubtle)
      }
      this.y += 4.8
      this.textAt(entry.project.name, x + pad, 'medium', 8.5, COLORS.heading)
      this.font('normal', 8.5, COLORS.body)
      this.pdf.text(
        entry.project.numberOfPages.toLocaleString(),
        pagesX,
        this.y,
        { align: 'right' },
      )
      this.font('medium', 8.5, COLORS.heading)
      this.pdf.text(money(entry.amount), amountX, this.y, { align: 'right' })
      this.y += 2.2
    })

    this.hline()
    this.y += 5.5
    this.textAt('Total', x + pad, 'bold', 9, COLORS.heading)
    this.font('bold', 10, COLORS.accent)
    this.pdf.text(money(data.billableAmount), amountX, this.y, {
      align: 'right',
    })

    this.y += 8
    this.textAt(
      `Amounts estimate ${MINUTES_PER_PAGE} min/page of review at $${RATE_PER_HOUR}/hr.`,
      x + pad,
      'normal',
      7,
      COLORS.muted,
    )
    this.y += 8
  }

  async drawFooter(logoPng: string) {
    const now = formatDate(new Date(), 'MMMM d, yyyy')

    this.hline()
    this.y += 5

    const logoW = 22
    const logoH = 4.6
    const footerText = `Report generated on ${now}`
    this.font('normal', 7.5, COLORS.muted)
    const textW = this.pdf.getTextWidth(footerText)
    const totalW = logoW + 3 + textW
    const startX = this.m + (this.w - totalW) / 2

    this.pdf.addImage(logoPng, 'PNG', startX, this.y - 3.2, logoW, logoH)
    this.textAt(footerText, startX + logoW + 3, 'normal', 7.5, COLORS.muted)
  }

  save(filename: string) {
    this.pdf.save(filename)
  }
}

export async function exportAccountSummaryPDF(
  data: AccountSummaryData,
): Promise<void> {
  const logoPng = await svgToPng('/logo.svg', 143, 30)

  const report = new PDFReport()
  report.drawHeader(data)
  report.drawMetrics(data)
  report.drawCaseTable(data)
  await report.drawFooter(logoPng)

  const date = new Date().toISOString().split('T')[0]
  report.save(`billing-statement-${date}.pdf`)
}
